import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ThumbsUp, AlertCircle, FileText, LogOut, Play, Pause, Star, Hash, Download, Award } from 'lucide-react';
import { computeQuestionNumbers } from '@/lib/questionNumbering';
import ComparisonHeader from '@/components/ComparisonHeader';
import { NpsFace } from '@/components/NpsFace';
import {
  cleanQuestionLabel, getQuestionType, getQuestionLabel,
  normaliseYesNoAnswer, normaliseNpsAnswer, normaliseNumberAnswer,
  answerComment,
  DISPLAY_ONLY_TYPES, CHOICE_TYPES, RATING_TYPES, NPS_TYPES, NUMBER_TYPES,
} from '@/lib/answerFormatting';
import '@/styles/reportPrint.css';

const SATISFIND_LOGO_URL = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/s8cmwdge_satisFIND%2020th%20Logo.png';

function fmtDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: '2-digit' });
  } catch { return s; }
}

function asString(v) {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(x => (typeof x === 'object' && x && (x.label || x.text)) ? (x.label || x.text) : String(x)).join(', ');
  if (typeof v === 'object') {
    if (v.label) return v.label;
    if (v.text) return v.text;
    if ('value' in v) return asString(v.value);
    return '';
  }
  return String(v);
}

function toAbsUrl(u) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!u) return '';
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/api/')) return base + u;
  return u;
}

function AudioPlayer({ url }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);
  const resolvedUrl = toAbsUrl(url);
  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(resolvedUrl);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);
  return (
    <button onClick={toggle} className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium bg-[#D62B27]/10 text-[#D62B27] hover:bg-[#D62B27]/20" data-testid="report-audio">
      {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      {playing ? 'Pause' : 'Play recording'}
    </button>
  );
}

function StarsDisplay({ value, max = 5 }) {
  const n = Math.max(0, Math.min(max, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? 'fill-[#F1D511] text-[#F1D511]' : 'text-[#E2E8F0]'}`} />
      ))}
      <span className="text-xs text-[#475569] ml-1.5 font-medium">{n}/{max}</span>
    </span>
  );
}

function NpsBadge({ value, presentation }) {
  if (value == null) return null;
  const tone = value >= 9 ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : value >= 7 ? 'bg-amber-100 text-amber-700 border-amber-300'
    : 'bg-rose-100 text-rose-700 border-rose-300';
  const label = value >= 9 ? 'Promoter' : value >= 7 ? 'Passive' : 'Detractor';
  // Show the face only when this report's captured presentation used faces.
  const showFace = presentation && (presentation.style === 'faces' || presentation.style === 'faces_numbers');
  return (
    <span className="inline-flex items-center gap-2">
      {showFace && <NpsFace score={value} appearance={presentation.appearance || 'color'} size={34} />}
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-base border ${tone}`}>{value}</span>
      <span className="text-[11px] text-[#64748B]">{label}</span>
    </span>
  );
}

// Tone helper — score ratio (0..1) → tag-aware pill colour classes.
function scoreTone(ratio) {
  if (ratio === null || ratio === undefined) return 'bg-slate-100 text-slate-600 border-slate-200';
  if (ratio >= 1) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (ratio > 0) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
}

// Iter26 dimension pill — small accent badge next to a tagged question.
const DIM_STYLES = {
  brand:   { label: 'Brand Excellence',   bg: '#E6F1FB', fg: '#0C447C' },
  service: { label: 'Service Excellence', bg: '#E1F5EE', fg: '#085041' },
  sales:   { label: 'Sales Excellence',   bg: '#FAEEDA', fg: '#633806' },
};
function DimensionPill({ dim }) {
  const style = DIM_STYLES[dim];
  if (!style) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border border-transparent"
      style={{ backgroundColor: style.bg, color: style.fg }}
      data-testid={`report-q-dim-${dim}`}
      title={style.label}
    >
      {style.label}
    </span>
  );
}

function YesNoPill({ value, scoreRatio }) {
  // value is "Yes" / "No" / "N/A" (already normalised) or a raw fallback label.
  // Iter26 (rule 2a — tag-aware): when scoreRatio is provided (tagged question),
  // colour the pill by the graded ratio. Otherwise neutral.
  if (!value) return <span className="text-xs text-[#94A3B8] italic">No response</span>;
  const tone = scoreRatio === undefined
    ? 'bg-slate-100 text-slate-600 border-slate-200'   // not tagged → neutral
    : scoreTone(scoreRatio);
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${tone}`}>
      {value}
    </span>
  );
}

// Iter26: per-question graded score badge. Shows X/N (P%).
function QuestionScoreBadge({ earned, max }) {
  if (earned === null || earned === undefined || !max) return null;
  const ratio = earned / max;
  const tone = scoreTone(ratio);
  const pct = Math.round(ratio * 100);
  const icon = ratio >= 1 ? '✓' : ratio > 0 ? '◐' : '✗';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider align-middle border ${tone}`}
      data-testid={
        ratio >= 1 ? 'report-q-score-yes'
        : ratio === 0 ? 'report-q-score-no'
        : 'report-q-score-partial'
      }
      title={`Scored ${earned}/${max} (${pct}%)`}
    >
      {icon} {earned}/{max}
    </span>
  );
}

// Iter26: per-section subtotal pill — graded points / max points.
function SectionScorePill({ score }) {
  if (!score || !score.max) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#EEF2F6] text-[#475569] border border-[#CBD5E1]"
      data-testid="report-section-score"
      title={`${score.earned}/${score.max} points across tagged questions in this section`}
    >
      Section score: {score.earned}/{score.max} ({score.pct}%)
    </span>
  );
}

function AnswerRow({ q, raw, derivedTranscript, number, score, sectionScore, dim, npsPresentation }) {
  const qtype = getQuestionType(q);
  const qtext = getQuestionLabel(q);
  const sub = q.sub_text || q.help_text || q.subtitle || q.description || '';
  const cleanSub = sub ? cleanQuestionLabel(sub) : '';

  // Section header = visual divider only.
  if (qtype === 'section_header') {
    return (
      <div className="pt-5 pb-2" data-testid={`report-section-${q.question_id || q.id}`}>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3E6F]">{number ? `Section ${number}` : 'Section'}</span>
          <div className="flex-1 h-px bg-[#1A3E6F]/20" />
          <SectionScorePill score={sectionScore} />
        </div>
        {qtext && <h3 className="font-['Montserrat'] text-lg font-bold text-[#0F172A] mt-2">{qtext}</h3>}
        {cleanSub && <p className="text-sm text-[#64748B] mt-0.5">{cleanSub}</p>}
      </div>
    );
  }

  // Other display-only blocks (welcome / thank you / page break / rich_text / image_display)
  if (DISPLAY_ONLY_TYPES.has(qtype)) {
    // Suppress welcome/thank you on the client report — they aren't useful here.
    if (['welcome', 'thank_you', 'page_break', 'confirmation'].includes(qtype)) return null;
    return (
      <div className="border-l-2 border-[#CBD5E1] bg-[#F8FAFC] rounded-r px-4 py-2 mt-4">
        {qtext ? <p className="text-sm font-semibold text-[#475569]">{qtext}</p> : null}
        {cleanSub && <p className="text-xs text-[#64748B] mt-0.5">{cleanSub}</p>}
      </div>
    );
  }

  // Voice
  // Feb 2026 — answers coming from SurveyForm are stored as
  // `{text, audio_url, duration}` (see VoiceRecorder in SurveyForm.js).
  // Older code paths / imports may write the transcript under
  // `transcription`. The server-side safety net stores post-hoc
  // transcripts in `derived_transcripts` (never mutating the raw
  // answer). Prefer the transcript submitted with the original answer,
  // then the derived transcript.
  const isVoice = raw && typeof raw === 'object' && !Array.isArray(raw) && (raw.audio_url || raw.transcription != null || raw.text != null && (qtype === 'voice' || qtype === 'media_question'));
  const voiceTranscript = isVoice
    ? (raw.transcription || raw.text || derivedTranscript || '')
    : '';
  // File upload
  const isFiles = qtype === 'file_upload' && Array.isArray(raw);
  // Comment overlay
  const comment = answerComment(raw);
  // Rating
  const ratingValue = RATING_TYPES.has(qtype) ? (
    typeof raw === 'number' ? raw
    : (raw && typeof raw === 'object' ? (raw.score_value ?? raw.value) : null)
  ) : null;
  const ratingMax = q.scale_max || 5;
  // NPS
  const npsValue = NPS_TYPES.has(qtype) ? normaliseNpsAnswer(raw) : null;
  // Number
  const numberValue = NUMBER_TYPES.has(qtype) ? normaliseNumberAnswer(raw) : null;
  // Yes/No
  const yesNoValue = qtype === 'yes_no' ? normaliseYesNoAnswer(raw) : null;
  // Other choice
  const otherChoiceLabel = (CHOICE_TYPES.has(qtype) && qtype !== 'yes_no' && raw != null) ? (
    Array.isArray(raw) ? raw.map(x => asString(x)).filter(Boolean).join(', ')
    : (typeof raw === 'object' ? (raw.label || asString(raw.value)) : String(raw))
  ) : null;

  const empty = raw == null
    || (Array.isArray(raw) && !raw.length)
    || (typeof raw === 'string' && !raw.trim())
    || (typeof raw === 'object' && !Array.isArray(raw) && !Object.keys(raw).length);

  return (
    <div className="py-3 border-b border-[#F1F5F9] last:border-b-0" data-testid={`report-answer-${q.question_id || q.id}`}>
      <p className="text-sm font-semibold text-[#0F172A] mb-1.5 flex items-center gap-2 flex-wrap">
        <span>{number ? `${number}. ` : ''}{qtext || <span className="text-[#94A3B8] italic">Untitled question</span>}</span>
        {q.qualifier_id && score ? <QuestionScoreBadge earned={score.earned} max={score.max} /> : null}
        {q.qualifier_id && dim ? <DimensionPill dim={dim} /> : null}
        {q.qualifier_id && !score ? (
          <span
            className="inline-block text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8] italic"
            title="This question has an Excellence tag but its answer is not counted (unanswered, N/A, or an unscoreable answer type)."
            data-testid={`report-q-excluded-${q.question_id || q.id}`}
          >Excluded from scoring</span>
        ) : null}
      </p>

      {empty && !yesNoValue ? (
        <p className="text-xs text-[#94A3B8] italic">No response</p>
      ) : isVoice ? (
        <div className="space-y-2">
          {raw.audio_url && <AudioPlayer url={raw.audio_url} />}
          {voiceTranscript && <p className="text-sm text-[#334155] italic">&ldquo;{voiceTranscript}&rdquo;</p>}
        </div>
      ) : isFiles ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
          {raw.filter(f => f && f.url).map((f, i) => {
            const url = toAbsUrl(f.url);
            const isImg = (f.type || '').startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(url);
            const isVid = (f.type || '').startsWith('video/') || /\.(mp4|mov|webm)$/i.test(url);
            return (
              <div key={i} className="block">
                {isImg ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="" className="w-full h-32 object-cover rounded border border-[#E2E8F0] hover:border-[#1A3E6F]" />
                  </a>
                ) : isVid ? (
                  <>
                    <video controls src={url} className="w-full rounded border border-[#E2E8F0] bg-black" style={{ maxHeight: 200 }} preload="metadata">
                      Your browser does not support the video tag.
                    </video>
                    <span className="video-print-placeholder text-xs">[Video attachment — view online]</span>
                  </>
                ) : (
                  <a href={url} target="_blank" rel="noreferrer" className="h-32 flex items-center justify-center bg-[#F8FAFC] rounded border border-[#E2E8F0] text-[#64748B]">
                    <FileText className="w-6 h-6" />
                  </a>
                )}
                {f.comment && (
                  <div className="mt-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B] mb-1">
                      Comment
                    </p>
                    <p className="text-sm text-[#0F172A] whitespace-pre-wrap break-words">
                      {f.comment}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : ratingValue != null ? (
        <StarsDisplay value={ratingValue} max={ratingMax} />
      ) : npsValue != null ? (
        <NpsBadge value={npsValue} presentation={npsPresentation} />
      ) : numberValue != null ? (
        <span className="inline-flex items-center gap-2 text-sm">
          <Hash className="w-3.5 h-3.5 text-[#94A3B8]" />
          <span className="font-semibold text-[#0F172A]">{String(numberValue)}</span>
        </span>
      ) : qtype === 'yes_no' ? (
        <YesNoPill
          value={yesNoValue}
          scoreRatio={q.qualifier_id && score && score.max ? score.earned / score.max : undefined}
        />
      ) : otherChoiceLabel != null ? (
        <span
          className={`inline-block px-2.5 py-0.5 rounded text-sm font-medium border ${
            q.qualifier_id && score && score.max
              ? scoreTone(score.earned / score.max)
              : 'bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]'
          }`}
        >
          {otherChoiceLabel}
        </span>
      ) : (
        <p className="text-sm text-[#334155] whitespace-pre-wrap">{asString(raw)}</p>
      )}

      {/* Optional comment overlay (rating / choice etc.) */}
      {comment && !isVoice && !isFiles && (
        <p className="text-sm text-[#475569] mt-1.5 italic">&ldquo;{comment}&rdquo;</p>
      )}
    </div>
  );
}

function TrustXSection({ trustx, onOpenDimension, onOpenTrustx, onOpenGuide }) {
  if (!trustx || !trustx.has_any) return null;

  // Format helpers — round earned/max to 1 dp so 3.0 renders as "3" and
  // 3.5 renders as "3.5", matching the badge convention elsewhere.
  const fmt = (n) => (n == null ? '—' : (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)));

  const dims = [
    { key: 'brand',   label: 'Brand Excellence',   dim: trustx.brand,   bg: '#E6F1FB', fg: '#0C447C' },
    { key: 'sales',   label: 'Sales Excellence',   dim: trustx.sales,   bg: '#FAEEDA', fg: '#633806' },
    { key: 'service', label: 'Service Excellence', dim: trustx.service, bg: '#E1F5EE', fg: '#085041' },
  ];

  // TrustX overall earned/max = sum of the three dimensions.
  const overallEarned = (trustx.brand?.earned || 0) + (trustx.sales?.earned || 0) + (trustx.service?.earned || 0);
  const overallMax    = (trustx.brand?.max    || 0) + (trustx.sales?.max    || 0) + (trustx.service?.max    || 0);

  return (
    <section className="report-section bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-5" data-testid="report-trustx">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">TrustX<sup>®</sup> Score</p>
          <button
            type="button"
            onClick={onOpenTrustx}
            className="group text-left hover:opacity-80 transition-opacity"
            data-testid="report-trustx-open"
            title="How TrustX is calculated"
          >
            <p className="font-['Montserrat'] text-5xl font-bold text-[#0F172A] mt-1 group-hover:underline decoration-2 underline-offset-4" data-testid="report-trustx-overall">
              {trustx.overall == null ? '—' : `${trustx.overall}%`}
            </p>
            {overallMax > 0 && (
              <p className="text-xs text-[#64748B] mt-1" data-testid="report-trustx-fraction">
                {fmt(overallEarned)} / {fmt(overallMax)} across all Excellence questions
              </p>
            )}
            <p className="text-[11px] text-[#1A3E6F] mt-1 font-semibold">How this is calculated ›</p>
          </button>
        </div>
        <div className="flex flex-col items-end gap-2 max-w-sm">
          <p className="text-xs text-[#64748B]">
            Weighted total of all tagged Brand / Sales / Service questions with a scoreable answer.
            N/A and unanswered questions are excluded from both numerator and denominator.
          </p>
          <button
            type="button"
            onClick={onOpenGuide}
            className="text-xs font-semibold text-[#1A3E6F] hover:underline"
            data-testid="report-view-scoring-guide"
          >View Scoring Guide ›</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {dims.map(d => (
          <button
            key={d.key}
            type="button"
            onClick={() => onOpenDimension(d.key)}
            className="trustx-card rounded-lg px-5 py-4 border border-transparent text-left hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A3E6F] transition"
            style={{ backgroundColor: d.bg, color: d.fg }}
            data-testid={`report-trustx-${d.key}`}
            aria-label={`View ${d.label} breakdown`}
          >
            <p className="trustx-card-label text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{d.label}</p>
            <p className="font-['Montserrat'] text-3xl font-bold mt-1">
              {d.dim?.pct == null ? '—' : `${d.dim.pct}%`}
            </p>
            {(d.dim?.max || 0) > 0 && (
              <p
                className="text-[11px] mt-1 opacity-80"
                data-testid={`report-trustx-${d.key}-fraction`}
              >
                {fmt(d.dim.earned)} / {fmt(d.dim.max)} scored points
              </p>
            )}
            <p className="text-[11px] mt-1 font-semibold opacity-90">View breakdown ›</p>
          </button>
        ))}
      </div>
    </section>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Scoring Breakdown Drawer — Feb 2026 (Iter30.1)
// ══════════════════════════════════════════════════════════════════════
// Slides in from the right. Three modes:
//   • 'dimension' → Per-FIND breakdown of Brand / Sales / Service, listing
//     every scoreable question that contributes to the dimension total.
//   • 'trustx' → Per-FIND rollup showing dimension × dimension → total.
//   • 'guide' → Survey-level Scoring Guide fetched from
//     GET /api/reports/scoring-guide/{survey_id}. Explains scoring
//     structure independent of any specific FIND.
// All content is derived from the report payload + Survey Builder — no
// hard-coded questions, no separate scoring config.
// ══════════════════════════════════════════════════════════════════════
function ScoringDrawer({ open, mode, dimKey, report, trustx, gradeAnswer, onClose }) {
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState(null);

  React.useEffect(() => {
    if (!open || mode !== 'guide' || !report?.survey_id) return;
    setGuideLoading(true);
    setGuideError(null);
    (async () => {
      try {
        const r = await fetch(`${API}/reports/scoring-guide/${encodeURIComponent(report.survey_id)}`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error(`Scoring guide fetch failed (${r.status})`);
        setGuide(await r.json());
      } catch (e) {
        setGuideError(String(e.message || e));
      } finally {
        setGuideLoading(false);
      }
    })();
  }, [open, mode, report?.survey_id]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const DIM_LABELS = {
    brand:   { label: 'Brand Excellence',   bg: '#E6F1FB', fg: '#0C447C' },
    sales:   { label: 'Sales Excellence',   bg: '#FAEEDA', fg: '#633806' },
    service: { label: 'Service Excellence', bg: '#E1F5EE', fg: '#085041' },
  };
  const fmt = (n) => (n == null ? '—' : (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)));

  const renderAnswer = (q, raw) => {
    if (raw == null) return <span className="italic text-[#94A3B8]">No response</span>;
    if (typeof raw === 'string') return raw || <span className="italic text-[#94A3B8]">Empty</span>;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (typeof raw === 'object') {
      if (raw.label) return String(raw.label);
      if ('value' in raw) return String(raw.value);
      if ('score_value' in raw) return String(raw.score_value);
      if ('text' in raw) return String(raw.text);
      return JSON.stringify(raw);
    }
    return String(raw);
  };

  // ── mode: dimension ────────────────────────────────────────────────
  const renderDimension = () => {
    const dim = trustx?.[dimKey];
    if (!dim) return <p className="text-sm text-[#64748B]">No data for this dimension.</p>;
    const meta = DIM_LABELS[dimKey];
    const qidToDim = {};
    for (const qual of (report?.qualifiers || [])) {
      const key = { 'brand excellence': 'brand', 'service excellence': 'service', 'sales excellence': 'sales' }[
        (qual?.label || '').trim().toLowerCase()
      ];
      if (key && qual?.id) qidToDim[qual.id] = key;
    }
    const scored = [];
    const excluded = [];
    for (const q of (report?.questions || [])) {
      if (qidToDim[q.qualifier_id] !== dimKey) continue;
      const raw = (report.answers || {})[q.question_id || q.id];
      const s = gradeAnswer(q, raw);
      if (s) scored.push({ q, raw, s });
      else   excluded.push({ q, raw });
    }
    return (
      <>
        <div className="rounded-lg px-4 py-3" style={{ backgroundColor: meta.bg, color: meta.fg }}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{meta.label}</p>
          <p className="font-['Montserrat'] text-3xl font-bold mt-1">{dim.pct == null ? '—' : `${dim.pct}%`}</p>
          <p className="text-xs mt-1 opacity-90">{fmt(dim.earned)} / {fmt(dim.max)} scored points</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mt-4 mb-2">Questions contributing to this score</p>
          {scored.length === 0 && <p className="text-sm text-[#64748B]">No scoreable answers.</p>}
          <ul className="space-y-3" data-testid={`drawer-dim-${dimKey}-questions`}>
            {scored.map(({ q, raw, s }) => (
              <li key={q.question_id || q.id} className="border border-[#E2E8F0] rounded-md p-3">
                <p className="text-sm font-semibold text-[#0F172A]">
                  {q.order ? `${q.order}. ` : ''}{getQuestionLabel(q) || 'Untitled question'}
                </p>
                <div className="flex items-center justify-between mt-1.5 gap-3">
                  <p className="text-sm text-[#475569]"><span className="text-[#94A3B8]">Answer: </span>{renderAnswer(q, raw)}</p>
                  <span className="inline-block text-[11px] font-mono font-semibold bg-[#F1F5F9] text-[#0F172A] rounded px-2 py-0.5 whitespace-nowrap">
                    {fmt(s.earned)} / {fmt(s.max)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {excluded.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mt-4 mb-2">Excluded from scoring</p>
              <ul className="space-y-2" data-testid={`drawer-dim-${dimKey}-excluded`}>
                {excluded.map(({ q, raw }) => (
                  <li key={q.question_id || q.id} className="text-xs text-[#64748B] border border-dashed border-[#E2E8F0] rounded-md p-2">
                    <span className="font-semibold text-[#334155]">{q.order ? `${q.order}. ` : ''}{getQuestionLabel(q) || 'Untitled'}</span>
                    <span className="ml-2 italic">→ {raw == null ? 'unanswered' : 'answer not scoreable (N/A or free text)'}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </>
    );
  };

  // ── mode: trustx rollup ────────────────────────────────────────────
  const renderTrustx = () => {
    if (!trustx) return null;
    const overallEarned = (trustx.brand?.earned || 0) + (trustx.sales?.earned || 0) + (trustx.service?.earned || 0);
    const overallMax    = (trustx.brand?.max    || 0) + (trustx.sales?.max    || 0) + (trustx.service?.max    || 0);
    return (
      <>
        <div className="rounded-lg px-4 py-3 bg-[#0F172A] text-white">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">TrustX<sup>®</sup> Score</p>
          <p className="font-['Montserrat'] text-3xl font-bold mt-1">{trustx.overall == null ? '—' : `${trustx.overall}%`}</p>
          <p className="text-xs mt-1 opacity-90">{fmt(overallEarned)} / {fmt(overallMax)} scored points</p>
        </div>
        <ul className="mt-4 space-y-2" data-testid="drawer-trustx-rollup">
          {['brand', 'sales', 'service'].map(k => (
            <li key={k} className="flex items-center justify-between text-sm border border-[#E2E8F0] rounded-md px-3 py-2">
              <span className="font-semibold text-[#0F172A]">{DIM_LABELS[k].label}</span>
              <span className="font-mono text-[13px] text-[#475569]">{fmt(trustx[k]?.earned)} / {fmt(trustx[k]?.max)}</span>
            </li>
          ))}
          <li className="flex items-center justify-between text-sm border-2 border-[#0F172A] rounded-md px-3 py-2 bg-[#F8FAFC]">
            <span className="font-bold text-[#0F172A]">Total</span>
            <span className="font-mono text-[13px] font-bold text-[#0F172A]">
              {fmt(overallEarned)} / {fmt(overallMax)} = {trustx.overall == null ? '—' : `${trustx.overall}%`}
            </span>
          </li>
        </ul>
        <p className="text-xs text-[#64748B] mt-4">
          TrustX<sup>®</sup> is the weighted total of all scoreable questions assigned to Brand,
          Sales and Service Excellence. It is not a simple average of the three percentages.
        </p>
      </>
    );
  };

  // ── mode: scoring guide ────────────────────────────────────────────
  const renderGuide = () => {
    if (guideLoading) return <p className="text-sm text-[#64748B]">Loading scoring guide…</p>;
    if (guideError)   return <p className="text-sm text-rose-600">{guideError}</p>;
    if (!guide)       return null;
    return (
      <div className="space-y-5" data-testid="drawer-scoring-guide">
        <p className="text-xs text-[#64748B]">
          This guide explains how <strong>{guide.survey_name || 'this survey'}</strong> is scored.
          Content is generated dynamically from the Survey Builder — changing a question,
          its options, or its Excellence qualifier will update this guide automatically.
        </p>
        {(guide.dimensions || []).map(dim => {
          const meta = DIM_LABELS[dim.key];
          return (
            <div key={dim.key} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="px-4 py-2" style={{ backgroundColor: meta.bg, color: meta.fg }}>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold">{meta.label}</p>
                <p className="text-sm font-semibold">Possible points: {dim.possible_points}</p>
              </div>
              {dim.questions.length === 0 ? (
                <p className="px-4 py-3 text-sm text-[#64748B] italic">No questions tagged in this dimension.</p>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {dim.questions.map(q => (
                    <li key={q.question_id} className="px-4 py-3">
                      <p className="text-sm font-semibold text-[#0F172A]">
                        {q.question_number ? `${q.question_number}. ` : ''}{cleanQuestionLabel(q.question_text) || 'Untitled'}
                      </p>
                      <p className="text-xs text-[#64748B] mt-1">
                        <span className="uppercase tracking-wider">{q.question_type.replace('_', ' ')}</span>
                        <span className="mx-2">·</span>
                        Max points: <span className="font-mono">{q.max_points}</span>
                      </p>
                      {q.scoring_note && (
                        <p className="text-[11px] text-[#475569] mt-1 font-mono">{q.scoring_note}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        <p className="text-xs text-[#64748B] italic border-l-2 border-[#1A3E6F] pl-3">
          {guide.trustx_explanation}
        </p>
      </div>
    );
  };

  const title = mode === 'trustx' ? 'TrustX Breakdown'
              : mode === 'guide'  ? 'Scoring Guide'
              : `${DIM_LABELS[dimKey]?.label || 'Excellence'} Breakdown`;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="scoring-drawer"
    >
      <div
        className="absolute inset-0 bg-[#0F172A]/40"
        onClick={onClose}
        data-testid="scoring-drawer-backdrop"
      />
      <div className="relative bg-white w-full max-w-xl h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
          <h2 className="font-['Montserrat'] text-lg font-bold text-[#0F172A]" data-testid="scoring-drawer-title">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F1F5F9] flex items-center justify-center text-[#64748B] hover:text-[#0F172A]"
            aria-label="Close"
            data-testid="scoring-drawer-close"
          >✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {mode === 'dimension' && renderDimension()}
          {mode === 'trustx'    && renderTrustx()}
          {mode === 'guide'     && renderGuide()}
        </div>
      </div>
    </div>
  );
}

export default function ReportView() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Scoring drawer state — one drawer, three possible modes.
  const [drawer, setDrawer] = useState({ open: false, mode: null, dimKey: null });
  const openDim    = (k) => setDrawer({ open: true, mode: 'dimension', dimKey: k });
  const openTrustx = ()  => setDrawer({ open: true, mode: 'trustx',    dimKey: null });
  const openGuide  = ()  => setDrawer({ open: true, mode: 'guide',     dimKey: null });
  const closeDrawer = () => setDrawer({ open: false, mode: null, dimKey: null });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`${API}/reports/${encodeURIComponent(responseId)}`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        setReport(d);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [responseId]);

  const numberMap = useMemo(
    () => computeQuestionNumbers(report?.questions || []),
    [report]
  );

  // Iter26 — Graded position-based scoring for tagged questions.
  // Walk questions in display order, track current section_header, and
  // for each tagged (qualifier_id != null) yes_no / single_choice /
  // dropdown question compute (earned, max) using the SAME rule as the
  // backend `_grade_tagged_answer` helper. Returns:
  //   questionScores: { [qid]: { earned, max } | undefined }
  //   sectionScores:  { [section_header_qid]: { earned, max, pct } }
  //   qidToDim:       { [qualifier_id]: 'brand' | 'service' | 'sales' }
  const TRUSTX_LABEL_MAP = { 'brand excellence': 'brand', 'service excellence': 'service', 'sales excellence': 'sales' };
  const optionLabel = (o) => {
    if (o == null) return '';
    if (typeof o === 'string') return o.trim();
    if (typeof o === 'object') return String(o.label || o.text || o.value || '').trim();
    return String(o).trim();
  };
  const gradeAnswer = (q, rawAnswer) => {
    const qtype = (q?.question_type || q?.type || '').trim().toLowerCase();
    if (rawAnswer == null) return null;
    if (qtype === 'yes_no') {
      const norm = normaliseYesNoAnswer(rawAnswer);
      if (norm === 'Yes') return { earned: 1, max: 1 };
      if (norm === 'No')  return { earned: 0, max: 1 };
      return null;
    }
    // ── Rating scale / star rating (Iter30) ────────────────────────────
    // Linear mapping: `max = max_value - min_value`, `earned = clamp(v,
    // min, max) - min_value`. Bounds resolved from
    // `rating_scale.min_value` / `rating_scale.max_value` first, falling
    // back through legacy fields, ultimately defaulting to (1, 5) to match
    // the display fallback used by `StarsDisplay` above.
    if (qtype === 'star_rating' || qtype === 'rating_scale') {
      const asInt = (v) => {
        if (v === null || v === undefined || typeof v === 'boolean') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const nested = q?.rating_scale || {};
      let lo = asInt(nested?.min_value);
      let hi = asInt(nested?.max_value);
      if (lo == null) lo = asInt(q?.min_value);
      if (hi == null) hi = asInt(q?.max_value);
      if (lo == null) lo = asInt(q?.scale_min);
      if (hi == null) hi = asInt(q?.scale_max);
      if (lo == null) lo = asInt(q?.min);
      if (hi == null) hi = asInt(q?.max);
      const ss = q?.star_settings || {};
      if (lo == null) lo = asInt(ss?.min_stars);
      if (hi == null) hi = asInt(ss?.max_stars);
      if (lo == null) lo = 1;
      if (hi == null) hi = 5;
      if (hi <= lo) hi = lo + 4;
      // Extract numeric value from raw answer envelope.
      let val = null;
      if (typeof rawAnswer === 'number') val = rawAnswer;
      else if (typeof rawAnswer === 'string') {
        const n = Number(rawAnswer);
        val = Number.isFinite(n) ? n : null;
      } else if (typeof rawAnswer === 'object') {
        for (const key of ['score_value', 'value', 'rating', 'stars']) {
          const v = rawAnswer?.[key];
          if (typeof v === 'number' && Number.isFinite(v)) { val = v; break; }
          if (typeof v === 'string') {
            const n = Number(v);
            if (Number.isFinite(n)) { val = n; break; }
          }
        }
      }
      if (val == null) return null;
      const max = hi - lo;
      if (max <= 0) return null;
      const clamped = Math.max(lo, Math.min(val, hi));
      // Historical-direction override (Iter30) — if the question declares
      // `rating_scale.historical_reversed_before` and this response was
      // submitted before that cutoff, invert the direction so the badge
      // matches the backend score.
      const cutoff = nested?.historical_reversed_before || q?.historical_reversed_before;
      const submissionDt = report?.submission_datetime || null;
      const _norm = (s) => (typeof s === 'string' && s.endsWith('Z') ? s.slice(0, -1) + '+00:00' : s);
      const earned = (cutoff && submissionDt && _norm(submissionDt) < _norm(cutoff))
        ? (hi - clamped)  // reversed
        : (clamped - lo); // normal
      return { earned, max };
    }
    if (qtype === 'single_choice' || qtype === 'dropdown') {
      // Exclude N/A and "Not Observed" options from the max-point calc AND
      // the index lookup so a Yes/No/N-A or Yes/No/Not-Observed question
      // scores 1/1 for "Yes" (not 2/2). True 3-option graded questions
      // (Excellent/Good/Poor) have no such option and stay 2/2 max.
      const NA_VARIANTS = [
        'n/a', 'na', 'not applicable',
        'not observed', 'not observed during visit', 'n.o.',
      ];
      const labels = (q.options || [])
        .map(optionLabel)
        .filter(Boolean)
        .map(s => s.toLowerCase())
        .filter(s => !NA_VARIANTS.includes(s));
      if (labels.length < 2) return null;
      let ansText = '';
      if (typeof rawAnswer === 'string') ansText = rawAnswer;
      else if (typeof rawAnswer === 'object' && !Array.isArray(rawAnswer)) {
        ansText = String(rawAnswer.label || rawAnswer.text || rawAnswer.value || '');
      } else if (Array.isArray(rawAnswer) && rawAnswer.length) {
        ansText = optionLabel(rawAnswer[0]);
      }
      const ansLc = ansText.trim().toLowerCase();
      if (!ansLc || NA_VARIANTS.includes(ansLc)) return null;
      const idx = labels.indexOf(ansLc);
      if (idx < 0) return null;
      const max = labels.length - 1;
      return { earned: max - idx, max };
    }
    return null;
  };

  const { questionScores, sectionScores, qidToDim } = useMemo(() => {
    const qs = report?.questions || [];
    const answers = report?.answers || {};
    const qualifiers = report?.qualifiers || [];
    const qid2dim = {};
    for (const q of qualifiers) {
      const lbl = (q?.label || '').trim().toLowerCase();
      const dim = TRUSTX_LABEL_MAP[lbl];
      if (dim && q?.id) qid2dim[q.id] = dim;
    }
    const qScores = {};
    const sScoresRaw = {};
    let currentSection = null;
    for (const q of qs) {
      const qtype = getQuestionType(q);
      const qid = q.question_id || q.id;
      if (qtype === 'section_header') {
        currentSection = qid;
        if (!sScoresRaw[currentSection]) sScoresRaw[currentSection] = { earned: 0, max: 0 };
        continue;
      }
      if (!q.qualifier_id) continue;
      const graded = gradeAnswer(q, answers[qid]);
      if (!graded || !graded.max) continue;
      qScores[qid] = graded;
      if (currentSection) {
        sScoresRaw[currentSection].earned += graded.earned;
        sScoresRaw[currentSection].max    += graded.max;
      }
    }
    const sScores = {};
    for (const [sid, s] of Object.entries(sScoresRaw)) {
      sScores[sid] = s.max === 0
        ? { earned: 0, max: 0, pct: null }
        : { earned: s.earned, max: s.max, pct: Math.round((s.earned / s.max) * 100) };
    }
    return { questionScores: qScores, sectionScores: sScores, qidToDim: qid2dim };
  }, [report]);

  // Client Experience V2 — server-generated PDF replaces the legacy
  // window.print() (which inlined full-resolution photos and produced
  // 30–35 MB files). The backend endpoint returns a compact
  // reportlab PDF; filename is `satisFIND_<public_id>.pdf` as per
  // spec. Falls back to window.print() only if the request fails,
  // so users are never blocked.
  const handleDownloadPDF = async () => {
    try {
      const pid = report?.public_id || report?.response_id;
      const r = await fetch(`${API}/reports/${pid}/pdf`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `satisFIND_${pid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed:', e);
      window.print();
    }
  };

  // Client Experience V2 — Staff Recognition dialog.
  // Service Excellence Certificate eligibility comes down inside the
  // report payload (`report.service_excellence`). We render the button
  // ONLY when `eligible: true`. The old branch-picker dialog is gone
  // for eligible tenants — branch comes from the FIND itself; the
  // Admin only enters the recipient's full name.
  const secEligibility = report?.service_excellence || null;
  const showRecognizeStaff = !!secEligibility?.eligible;

  const [recogOpen, setRecogOpen] = useState(false);
  const [recogName, setRecogName] = useState('');
  const [recogBusy, setRecogBusy] = useState(false);
  const [recogError, setRecogError] = useState(null);

  const handleIssueRecognition = async () => {
    if (!recogName.trim()) return;
    setRecogBusy(true);
    setRecogError(null);
    try {
      const pid = report?.public_id || report?.response_id;
      const r = await fetch(`${API}/reports/${pid}/service-excellence-certificate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: recogName.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `satisFIND_Service_Excellence_${pid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setRecogOpen(false);
      setRecogName('');
    } catch (e) {
      console.error('Recognition failed:', e);
      setRecogError(e.message || 'Failed to generate certificate.');
    } finally {
      setRecogBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#64748B]" data-testid="report-loading">Loading report…</div>;
  }

  if (notFound || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="report-not-available">
        <div className="text-center space-y-3">
          <FileText className="w-10 h-10 text-[#94A3B8] mx-auto" />
          <p className="text-base font-semibold text-[#0F172A]">This report is not available.</p>
          <button
            onClick={() => navigate('/reports')}
            className="text-sm text-[#1A3E6F] hover:underline inline-flex items-center gap-1"
            data-testid="report-back-not-available"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to reports
          </button>
        </div>
      </div>
    );
  }

  const satisaction = report.satisaction || { strengths: [], opportunities: [] };
  const hasStrengths = (satisaction.strengths || []).length > 0;
  const hasOpps = (satisaction.opportunities || []).length > 0;
  const showSatisaction = hasStrengths || hasOpps;
  const tenantLogo = report.tenant_branding?.logo || null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="report-view-page">
      {/* Sticky chrome — hidden in print */}
      <header className="no-print bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <img src={SATISFIND_LOGO_URL} alt="satisFIND" className="h-8" />
            {tenantLogo && (
              <>
                <span className="text-[#CBD5E1]">·</span>
                <img src={tenantLogo} alt={report.tenant_name || 'Tenant logo'} className="h-8 rounded" data-testid="report-tenant-logo" />
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#64748B] hidden sm:inline">{user?.email}</span>
            <Button size="sm" variant="ghost" onClick={() => { logout(); navigate('/auth'); }}>
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-5 pb-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/reports')} data-testid="report-back" className="-ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to reports
          </Button>
        </div>
      </header>

      {/* Print-only header (logos at top of first PDF page) */}
      <div className="print-header print-only">
        <img src={SATISFIND_LOGO_URL} alt="satisFIND" />
        {tenantLogo && <img src={tenantLogo} alt={report.tenant_name || ''} />}
      </div>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        {/* Identification block */}
        <section className="bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-3" data-testid="report-header">
          <ComparisonHeader
            answers={report.answers}
            surveyConfig={report.survey_config}
          />
          <h1
            className="font-['Montserrat'] text-3xl font-bold text-[#0F172A]"
            data-testid="report-title"
          >
            {/* Feb 2026 fix — comparison FINDs display "Comparison Report"
                as the page title regardless of the attached administrative
                branch. The two compared locations are still surfaced by
                <ComparisonHeader/> above. */}
            {report.survey_config?.is_comparison === true
              ? 'Comparison Report'
              : (report.branch_name || 'Mystery shopping report')}
          </h1>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Survey</p>
              <p className="text-[#0F172A]">{report.survey_name || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Visit date</p>
              <p className="text-[#0F172A]">{fmtDate(report.visit_date)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">FIND ID</p>
              <p className="text-[#0F172A] font-mono text-xs">{report.public_id || report.response_id}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Published</p>
              <p className="text-[#0F172A]">{fmtDate(report.published_at)}</p>
            </div>
          </div>
        </section>

        {/* TrustX® — hidden when no tagged questions */}
        <TrustXSection
          trustx={report.trustx}
          onOpenDimension={openDim}
          onOpenTrustx={openTrustx}
          onOpenGuide={openGuide}
        />

        {/* satisACTION® — hidden when both lists empty */}
        {showSatisaction && (
          <section className="report-section bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-5" data-testid="report-satisaction">
            <h2 className="font-['Montserrat'] text-xl font-bold text-[#0F172A]">satisACTION<sup>®</sup></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {hasStrengths && (
                <div className="satisaction-strength border-l-2 pl-4" style={{ borderColor: '#5DCAA5' }} data-testid="report-strengths">
                  <p className="satisaction-strength-label text-xs uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1 mb-2">
                    <ThumbsUp className="w-3.5 h-3.5" /> Strengths
                  </p>
                  <ul className="space-y-2 text-sm text-[#334155]">
                    {satisaction.strengths.map((b, i) => (<li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />{b}</li>))}
                  </ul>
                </div>
              )}
              {hasOpps && (
                <div className="satisaction-opportunity border-l-2 pl-4" style={{ borderColor: '#EF9F27' }} data-testid="report-opportunities">
                  <p className="satisaction-opportunity-label text-xs uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1 mb-2">
                    <AlertCircle className="w-3.5 h-3.5" /> Opportunities
                  </p>
                  <ul className="space-y-2 text-sm text-[#334155]">
                    {satisaction.opportunities.map((b, i) => (<li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />{b}</li>))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Answers */}
        <section className="report-section bg-white rounded-xl border border-[#E2E8F0] p-6" data-testid="report-answers">
          <h2 className="font-['Montserrat'] text-xl font-bold text-[#0F172A] mb-3">Survey Answers</h2>
          {(report.questions || []).map(q => {
            const qid = q.question_id || q.id;
            const qtype = getQuestionType(q);
            return (
              <div key={qid} className="q-row">
                <AnswerRow
                  q={q}
                  raw={(report.answers || {})[qid]}
                  derivedTranscript={(report.derived_transcripts || {})[qid]}
                  number={numberMap[qid]}
                  score={q.qualifier_id ? questionScores[qid] : undefined}
                  sectionScore={qtype === 'section_header' ? sectionScores[qid] : undefined}
                  dim={q.qualifier_id ? qidToDim[q.qualifier_id] : undefined}
                  npsPresentation={(report.nps_presentation || {})[qid]}
                />
              </div>
            );
          })}
        </section>

        {/* PDF download + Client Experience V2 recognition */}
        <div className="no-print flex justify-end gap-2">
          {showRecognizeStaff && (
            <Button
              variant="outline"
              onClick={() => setRecogOpen(true)}
              className="border-[#1A3E6F] text-[#1A3E6F]"
              data-testid="report-recognize-staff"
            >
              <Award className="w-4 h-4 mr-1.5" /> Recognize Staff
            </Button>
          )}
          <Button
            onClick={handleDownloadPDF}
            className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
            data-testid="report-download-pdf"
          >
            <Download className="w-4 h-4 mr-1.5" /> Download Report (PDF)
          </Button>
        </div>

        {/* Service Excellence Certificate dialog. Client Admin sees
            the original recognition answer READ-ONLY and only enters
            the recipient's full name — branch, date and Response ID
            are auto-populated from the FIND. */}
        <Dialog open={recogOpen} onOpenChange={setRecogOpen}>
          <DialogContent className="sm:max-w-[520px]" data-testid="recognition-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat']">Issue Service Excellence Certificate</DialogTitle>
              <DialogDescription>
                Recognize the staff member mentioned in this FIND. The certificate
                will use the branch and Response ID from this report — you only
                need to type their full name.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-[#64748B] uppercase tracking-wider">
                  satisFINDER’s recognition response
                </Label>
                <div
                  className="mt-1 p-3 rounded-md bg-[#F8FAFC] border border-[#E2E8F0]
                             text-sm text-[#0F172A] whitespace-pre-wrap"
                  data-testid="recognition-original-response"
                >
                  {secEligibility?.answer_text}
                </div>
              </div>
              <div>
                <Label htmlFor="recog-name">Staff member’s full name</Label>
                <Input
                  id="recog-name" value={recogName}
                  onChange={(e) => setRecogName(e.target.value)}
                  data-testid="recognition-full-name"
                  placeholder="e.g. Alex Santos"
                />
                <p className="text-[11px] text-[#64748B] mt-1">
                  Enter the employee’s full name exactly as you would like it to appear on the certificate.
                </p>
              </div>
              {recogError && (
                <p className="text-xs text-[#D62B27]" data-testid="recognition-error">
                  {recogError}
                </p>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={() => setRecogOpen(false)} disabled={recogBusy}>Cancel</Button>
              <Button
                onClick={handleIssueRecognition}
                disabled={recogBusy || !recogName.trim()}
                className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
                data-testid="recognition-confirm"
              >
                {recogBusy ? 'Generating…' : 'Generate Certificate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Web footer removed per Client Experience V2 cleanup — the
            "Generated by satisFIND" line is only meaningful on printed
            output and remains on the print-footer below. */}
      </main>

      {/* Running print footer */}
      <div className="print-footer print-only">satisFIND<sup>®</sup> · Confidential</div>

      {/* Scoring breakdown drawer (Feb 2026). Hidden in print. */}
      <div className="no-print">
        <ScoringDrawer
          open={drawer.open}
          mode={drawer.mode}
          dimKey={drawer.dimKey}
          report={report}
          trustx={report.trustx}
          gradeAnswer={gradeAnswer}
          onClose={closeDrawer}
        />
      </div>
    </div>
  );
}
